import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { logError } from '../utils/errorLogger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const isNorwegian = typeof navigator !== 'undefined' && navigator.language.startsWith('no');

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    logError('ErrorBoundary', error, {
      page: window.location.pathname,
      componentStack: info.componentStack ?? undefined,
    });
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="min-h-screen bg-[#0D1B2A] flex items-center justify-center p-6">
          <div className="max-w-md w-full text-center">
            <div className="text-5xl mb-6">⚠️</div>
            <h1 className="text-2xl font-bold text-[#FAFAF8] mb-3" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
              {isNorwegian ? 'Noe gikk galt' : 'Something went wrong'}
            </h1>
            <p className="text-[#FAFAF8]/60 mb-8 text-sm leading-relaxed">
              {isNorwegian
                ? 'En uventet feil oppstod. Prøv å laste siden på nytt.'
                : 'An unexpected error occurred. Try reloading the page.'}
            </p>
            <button
              onClick={this.handleRetry}
              className="px-6 py-3 bg-[#00C2A8] text-[#0D1B2A] font-semibold rounded-xl hover:bg-[#00C2A8]/90 transition-colors focus:outline-none focus:ring-2 focus:ring-[#00C2A8]/50"
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              {isNorwegian ? 'Prøv igjen' : 'Try again'}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
